
import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cancelBooking, fetchBookingById } from "@/lib/supabase";
import { BookingType } from "@/lib/supabase/types";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { AlertCircle, Calendar, CheckCircle, Clock, Info, MapPin, Users, AlertTriangle, DollarSign } from "lucide-react";
import { format, parseISO, isPast, addHours } from "date-fns";
import { CancelBookingDialog } from "@/components/dashboard/CancelBookingDialog";

const ManageBooking = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const bookingId = searchParams.get("id");
  const action = searchParams.get("action");
  
  // Use the manage-booking namespace and the default one
  const { t } = useTranslation(['manageBooking', 'translation']);
  
  const [booking, setBooking] = useState<BookingType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  
  useEffect(() => {
    const loadBooking = async () => {
      if (!bookingId) {
        setError("No booking ID provided");
        setIsLoading(false);
        return;
      }
      
      try {
        console.log("Attempting to fetch booking:", bookingId);
        const bookingData = await fetchBookingById(bookingId);
        console.log("Booking data received:", bookingData);
        setBooking(bookingData);
        
        // Automatically process cancel action if present in URL
        if (action === "cancel" && bookingData) {
          setShowCancelDialog(true);
        }
      } catch (err: any) {
        console.error("Error fetching booking:", err);
        setError(err.message || "Failed to load booking");
      } finally {
        setIsLoading(false);
      }
    };
    
    loadBooking();
  }, [bookingId, action]);
  
  const handleCancelBooking = async () => {
    if (!booking || !booking.id || !booking.classId) return;
    
    setIsCancelling(true);
    
    try {
      const result = await cancelBooking(booking.id, booking.classId);
      
      if (result.success) {
        toast.success("Your booking has been successfully cancelled");
        // Redirect to home page after short delay
        setTimeout(() => navigate("/"), 3000);
      } else {
        setError(result.error || "Failed to cancel booking");
        toast.error(result.error || "Failed to cancel booking");
      }
    } catch (err: any) {
      console.error("Error cancelling booking:", err);
      setError(err.message || "Failed to cancel booking");
      toast.error(err.message || "Failed to cancel booking");
    } finally {
      setIsCancelling(false);
      setShowCancelDialog(false);
    }
  };
  
  const isClassInPast = () => {
    if (booking?.classDate) {
      const classDate = parseISO(booking.classDate);
      
      // Parse time from string (e.g., "7:00 PM")
      let hours = 0;
      let minutes = 0;
      
      if (booking.classTime) {
        const timeMatch = booking.classTime.match(/(\d+):(\d+)\s*(AM|PM)?/i);
        if (timeMatch) {
          hours = parseInt(timeMatch[1], 10);
          minutes = parseInt(timeMatch[2], 10);
          
          if (timeMatch[3]?.toUpperCase() === 'PM' && hours < 12) {
            hours += 12;
          }
          if (timeMatch[3]?.toUpperCase() === 'AM' && hours === 12) {
            hours = 0;
          }
        }
      }
      
      // Set the exact class time
      const classDateTime = new Date(classDate);
      classDateTime.setHours(hours, minutes, 0, 0);
      
      // Add class duration (typically 2 hours)
      const classEndTime = addHours(classDateTime, 2);
      
      return isPast(classEndTime);
    }
    return false;
  };
  
  const isTooLateToCancel = () => {
    if (booking?.classDate) {
      const classDate = parseISO(booking.classDate);
      
      // Parse time component
      let hours = 0;
      let minutes = 0;
      
      if (booking.classTime) {
        const timeMatch = booking.classTime.match(/(\d+):(\d+)\s*(AM|PM)?/i);
        if (timeMatch) {
          hours = parseInt(timeMatch[1], 10);
          minutes = parseInt(timeMatch[2], 10);
          
          if (timeMatch[3]?.toUpperCase() === 'PM' && hours < 12) {
            hours += 12;
          }
          if (timeMatch[3]?.toUpperCase() === 'AM' && hours === 12) {
            hours = 0;
          }
        }
      }
      
      // Set the exact class time
      const classDateTime = new Date(classDate);
      classDateTime.setHours(hours, minutes, 0, 0);
      
      // Check if now + 24 hours is after class start time
      const now = new Date();
      const cancelDeadline = addHours(now, 24);
      
      return isPast(cancelDeadline) || isPast(classDateTime);
    }
    return false;
  };
  
  const renderBookingStatus = () => {
    if (isClassInPast()) {
      return (
        <Alert className="mb-4 bg-gray-100">
          <Info className="h-4 w-4" />
          <AlertTitle>{t('status.completed.title', { ns: 'manageBooking' })}</AlertTitle>
          <AlertDescription>
            {t('status.completed.description', { ns: 'manageBooking' })}
          </AlertDescription>
        </Alert>
      );
    }
    
    if (booking?.paymentStatus === 'pending') {
      return (
        <Alert className="mb-4 bg-amber-50">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t('status.paymentPending.title', { ns: 'manageBooking' })}</AlertTitle>
          <AlertDescription>
            {t('status.paymentPending.description', { ns: 'manageBooking' })}
          </AlertDescription>
        </Alert>
      );
    }
    
    if (isTooLateToCancel()) {
      return (
        <Alert className="mb-4 bg-amber-50">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{t('status.cancellationExpired.title', { ns: 'manageBooking' })}</AlertTitle>
          <AlertDescription>
            {t('status.cancellationExpired.description', { ns: 'manageBooking' })}
          </AlertDescription>
        </Alert>
      );
    }
    
    return (
      <Alert className="mb-4 bg-green-50">
        <CheckCircle className="h-4 w-4" />
        <AlertTitle>{t('status.confirmed.title', { ns: 'manageBooking' })}</AlertTitle>
        <AlertDescription>
          {t('status.confirmed.description', { ns: 'manageBooking' })}
        </AlertDescription>
      </Alert>
    );
  };

  const renderPaymentStatusBadge = () => {
    if (booking?.paymentStatus === 'paid') {
      return (
        <Badge variant="success" className="flex items-center gap-1 text-sm">
          <DollarSign className="h-3 w-3" />
          {t('paymentLabel.paid', { ns: 'manageBooking' })}
        </Badge>
      );
    } else if (booking?.paymentStatus === 'pending') {
      return (
        <Badge variant="default" className="bg-amber-100 text-amber-800 hover:bg-amber-100 flex items-center gap-1 text-sm">
          <AlertTriangle className="h-3 w-3" />
          {t('paymentLabel.pending', { ns: 'manageBooking' })}
        </Badge>
      );
    } else if (booking?.paymentStatus === 'nptr') {
      return (
        <Badge variant="destructive" className="flex items-center gap-1 text-sm">
          <AlertCircle className="h-3 w-3" />
          {t('paymentLabel.notPaid', { ns: 'manageBooking' })}
        </Badge>
      );
    } else {
      return (
        <span className="capitalize">{booking?.paymentStatus}</span>
      );
    }
  };
  
  if (isLoading) {
    return (
      <Layout>
        <div className="container max-w-3xl py-8">
          <Card>
            <CardHeader>
              <Skeleton className="h-8 w-2/3 mb-2" />
              <Skeleton className="h-4 w-1/2" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-24 mb-4" />
              <Skeleton className="h-16" />
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }
  
  if (error || !booking) {
    return (
      <Layout>
        <div className="container max-w-3xl py-8">
          <Card className="border-red-200">
            <CardHeader>
              <CardTitle className="text-red-600">{t('bookingNotFound.title', { ns: 'manageBooking' })}</CardTitle>
              <CardDescription>
                {error || t('bookingNotFound.description', { ns: 'manageBooking' })}
              </CardDescription>
            </CardHeader>
            <CardFooter>
              <Button onClick={() => navigate("/")}>{t('returnToHome', { ns: 'manageBooking' })}</Button>
            </CardFooter>
          </Card>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="container max-w-3xl py-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">{t('bookingDetails', { ns: 'manageBooking' })}</CardTitle>
            <CardDescription>
              {t('bookingId', { ns: 'manageBooking', id: booking.id.substring(0, 8) })}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {renderBookingStatus()}
            
            <div className="grid gap-4 md:grid-cols-2">
              <div className="flex flex-col space-y-1.5">
                <h3 className="text-sm font-medium text-gray-500">{t('class', { ns: 'manageBooking' })}</h3>
                <p className="font-medium">{booking.className}</p>
              </div>
              
              <div className="flex flex-col space-y-1.5">
                <h3 className="text-sm font-medium text-gray-500">{t('date', { ns: 'manageBooking' })}</h3>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  {booking.classDate ? (
                    <p>{format(parseISO(booking.classDate), "MMMM d, yyyy")}</p>
                  ) : (
                    <p>{booking.classDay}</p>
                  )}
                </div>
              </div>
              
              <div className="flex flex-col space-y-1.5">
                <h3 className="text-sm font-medium text-gray-500">{t('time', { ns: 'manageBooking' })}</h3>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-gray-500" />
                  <p>{booking.classTime}</p>
                </div>
              </div>
              
              {booking.groupSize > 1 && (
                <div className="flex flex-col space-y-1.5">
                  <h3 className="text-sm font-medium text-gray-500">{t('groupSize', { ns: 'manageBooking' })}</h3>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-gray-500" />
                    <p>{booking.groupSize} {t('people', { ns: 'manageBooking' })}</p>
                  </div>
                </div>
              )}
              
              <div className="flex flex-col space-y-1.5">
                <h3 className="text-sm font-medium text-gray-500">{t('bookedFor', { ns: 'manageBooking' })}</h3>
                <p>{booking.firstName} {booking.lastName}</p>
              </div>
              
              <div className="flex flex-col space-y-1.5">
                <h3 className="text-sm font-medium text-gray-500">{t('email', { ns: 'manageBooking' })}</h3>
                <p>{booking.email}</p>
              </div>
              
              {booking.phone && (
                <div className="flex flex-col space-y-1.5">
                  <h3 className="text-sm font-medium text-gray-500">{t('phone', { ns: 'manageBooking' })}</h3>
                  <p>{booking.phone}</p>
                </div>
              )}
              
              <div className="flex flex-col space-y-1.5">
                <h3 className="text-sm font-medium text-gray-500">{t('paymentStatus', { ns: 'manageBooking' })}</h3>
                {renderPaymentStatusBadge()}
              </div>
            </div>
          </CardContent>
          
          <CardFooter className="flex justify-between">
            <Button variant="outline" onClick={() => navigate("/")}>
              {t('returnToHome', { ns: 'manageBooking' })}
            </Button>
            
            {!isClassInPast() && !isTooLateToCancel() && (
              <Button 
                variant="destructive" 
                onClick={() => setShowCancelDialog(true)}
                disabled={isCancelling}
              >
                {t('cancelBooking', { ns: 'manageBooking' })}
              </Button>
            )}
          </CardFooter>
        </Card>
      </div>
      
      <CancelBookingDialog
        isOpen={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        onConfirm={handleCancelBooking}
        isCancelling={isCancelling}
      />
    </Layout>
  );
};

export default ManageBooking;
